import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Modal } from '@/components';
import { ModalProps } from '@mui/base/Modal';
import clsx from 'clsx';
import * as Yup from 'yup';
import { IIconImageFormData } from '@/types/icon-images-management.types';

interface IconImageModalProps extends Omit<ModalProps, 'children'> {
  mode: 'edit' | 'create';
  formData: IIconImageFormData;
  setFormData: React.Dispatch<React.SetStateAction<IIconImageFormData>>;
  errors: { [key: string]: string };
  setErrors: React.Dispatch<React.SetStateAction<{ [key: string]: string }>>;
  onSubmit: () => Promise<void>;
  isSubmitting: boolean;
}

// Keep in sync with saaristo_backend image.controller.ts ALLOWED_MIMETYPES
const ALLOWED_MIMETYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/svg+xml',
  'image/svg',
];
const MAX_FILE_SIZE = 5 * 1024 * 1024;

const validationSchema = Yup.object().shape({
  displayName: Yup.string().when('$mode', {
    is: 'create',
    then: (schema) =>
      schema
        .trim()
        .required('Name is required')
        .max(255, 'Name must be 255 characters or fewer'),
    otherwise: (schema) =>
      schema
        .trim()
        .max(255, 'Name must be 255 characters or fewer'),
  }),
  file: Yup.mixed()
    .when('$mode', {
      is: 'create',
      then: (schema) => schema.required('File is required'),
      otherwise: (schema) => schema.nullable(),
    })
    .test('fileSize', 'File size must be less than 5MB', (value) => {
      if (!value) return true;
      return (value as File).size <= MAX_FILE_SIZE;
    })
    .test('fileType', 'Only JPG, JPEG, PNG, SVG files are allowed', (value) => {
      if (!value) return true;
      return ALLOWED_MIMETYPES.includes((value as File).type);
    })
    .test(
      'editRequiresChange',
      'Provide a new name or image file',
      function validateEditChange(value) {
        if (this.options.context?.mode !== 'edit') {
          return true;
        }

        const { displayName, originalName } = this.parent as IIconImageFormData;
        const trimmedName = displayName?.trim() ?? '';
        const nameChanged =
          Boolean(trimmedName) && trimmedName !== (originalName ?? '').trim();

        return Boolean(value) || nameChanged;
      }
    ),
});

const IconImageModal = ({
  open,
  onClose,
  mode,
  formData,
  setFormData,
  errors,
  setErrors,
  onSubmit,
  isSubmitting,
  className,
}: IconImageModalProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateField = useCallback(
    async (name: keyof IIconImageFormData | string, value: unknown) => {
      try {
        await validationSchema.validateAt(
          name as string,
          { ...formData, [name]: value },
          { abortEarly: true, context: { mode } }
        );
        setErrors((prev) => ({ ...prev, [name]: '' }));
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          setErrors((prev) => ({
            ...prev,
            [name]: err.errors[0] || 'Invalid input',
          }));
        }
      }
    },
    [setErrors, formData, mode]
  );

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { files } = e.target;
      if (files && files.length > 0) {
        setFormData((prev) => ({ ...prev, file: files[0] }));
        validateField('file', files[0]);
      }
    },
    [setFormData, validateField]
  );

  const handleNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;
      setFormData((prev) => ({ ...prev, displayName: value }));
      validateField('displayName', value);
    },
    [setFormData, validateField]
  );

  const handleSubmit = useCallback(async () => {
    try {
      await validationSchema.validate(formData, {
        abortEarly: false,
        context: { mode },
      });
      await onSubmit();
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        const validationErrors: { [key: string]: string } = {};
        error.inner.forEach((err) => {
          if (err.path) validationErrors[err.path] = err.message;
        });
        setErrors(validationErrors);
      }
    }
  }, [formData, onSubmit, mode, setErrors]);

  const previewUrl = useMemo(() => {
    if (formData.file instanceof File) {
      return URL.createObjectURL(formData.file);
    }
    return formData.fileUrl || '';
  }, [formData.file, formData.fileUrl]);

  useEffect(() => {
    return () => {
      if (formData.file instanceof File && previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl, formData.file]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      className={clsx('!flex items-center justify-center', className)}
      aria-labelledby="icon-image-modal-title"
    >
      <div className="bg-gray-100 p-6 w-full max-w-md mx-4 rounded-lg shadow-lg flex flex-col max-h-[97dvh] overflow-y-auto">
        <h1 id="icon-image-modal-title" className="text-lg font-semibold mb-4">
          {mode === 'create' ? 'Add New Icon' : 'Edit Icon Image'}
        </h1>
        <div className="space-y-4">
          <div>
            <label
              htmlFor="displayName"
              className="block text-sm font-medium text-gray-700"
            >
              Name
            </label>
            <input
              id="displayName"
              name="displayName"
              type="text"
              value={formData.displayName}
              onChange={handleNameChange}
              disabled={mode === 'edit'}
              aria-invalid={!!errors.displayName}
              aria-describedby="displayName-error"
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              placeholder="e.g. Historic Trade Harbour"
            />
            {errors.displayName && (
              <p id="displayName-error" className="mt-1 text-sm text-red-600">
                {errors.displayName}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              {mode === 'create' ? 'Preview' : 'Current Preview'}
            </label>
            {previewUrl && (
              <img
                src={previewUrl}
                alt="Icon preview"
                style={{
                  width: '40px',
                  height: '40px',
                  objectFit: 'contain',
                  marginBottom: '10px',
                }}
              />
            )}
            <label
              htmlFor="file"
              className="block text-sm font-medium text-gray-700"
            >
              {mode === 'create' ? 'Icon File' : 'New Image File'}
            </label>
            <div className="flex items-center gap-2 mt-1">
              <button
                type="button"
                className="btn btn-sm btn-light"
                onClick={() => fileInputRef.current?.click()}
              >
                Choose Image
              </button>
              {formData.file instanceof File && (
                <span className="text-sm text-gray-600">
                  {formData.file.name}
                </span>
              )}
            </div>
            <input
              id="file"
              name="file"
              type="file"
              ref={fileInputRef}
              aria-invalid={!!errors.file}
              aria-describedby="file-error"
              className="hidden"
              onChange={handleFileChange}
              accept="image/jpeg,image/jpg,image/png,image/svg+xml"
            />

            {errors.file && (
              <p id="file-error" className="mt-1 text-sm text-red-600">
                {errors.file}
              </p>
            )}
          </div>
        </div>
        <div className="mt-8 flex justify-end gap-3">
          <button
            type="button"
            onClick={() => onClose?.({}, 'backdropClick')}
            className="btn btn-sm btn-ghost text-gray-600 hover:bg-gray-100 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-gray-500"
            aria-label="Cancel"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="btn btn-sm btn-primary bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            aria-label="Save"
            disabled={isSubmitting || !!errors.displayName || !!errors.file}
          >
            {isSubmitting ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export { IconImageModal };
